/**
 * Global runtime error boundary (mirrors web/src/app/AppErrorBoundary.tsx).
 *
 * Catches unexpected render-time errors anywhere in the tree and shows a safe
 * message (no raw stack traces, no secrets). The boundary never swallows
 * errors silently; it logs only a redacted summary.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log a redacted summary only — never the full stack with user data.
    console.error('[AppErrorBoundary]', error.name, error.message, {
      componentStack: info.componentStack?.slice(0, 200),
    });
  }

  override render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          className="flex min-h-screen items-center justify-center bg-background p-6"
        >
          <div className="w-full max-w-md rounded-lg border bg-surface p-6 shadow-sm text-center">
            <h1 className="text-lg font-semibold text-foreground">Something went wrong</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              An unexpected error occurred. Please reload the page.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
